/**
 * EVENT UTILITIES — Pub-Sub System
 *
 * Lightweight, global publish/subscribe event bus for decoupled component communication.
 * Replaces direct CustomEvent dispatching and addEventListener patterns with a centralized
 * message broker that supports sticky (replay) events for late-initializing subscribers.
 *
 * Publishers:
 *   product-overview.js, product-option-selectors.js, product-promotion.js, product-media.js,
 *   utilities-cart-state-manager.js, cart-drawer.js, cart-item.js, cart-items.js,
 *   welcome-package.js
 *
 * Subscribers:
 *   product-overview.js, product-form.js, product-price.js, product-option-selectors.js,
 *   product-option-drawer.js, product-promotion.js, product-media.js,
 *   swym-custom-add-to-wishlist.js, product-recommendations.js, payment-terms-container.js,
 *   truefit-widget.js, 3pa-findmine-pdp.js, 3pa-listrak.js, 3pa-twitter-analytics.js,
 *   utilities-cart-component-base.js, cart-drawer.js, cart-item.js, cart-items.js,
 *   cart-discounts.js, cart-gwp-selector.js, cart-note.js, free-shipping-banner.js,
 *   buy-more-save-more-banner.js, cart-tiered-spend-banner.js, membership-actions.js,
 *   welcome-package-modal.js, 3pa-findmine-cart.js, 3pa-truefit.js, membership-minilanding.js
 *
 * API (ES module — preferred):
 *   import { subscribe, publish, clearSticky } from '@utilities/events';
 *
 *   subscribe(eventName, callback)          → unsubscribe function
 *   publish(eventName, data, options?)      → void
 *   clearSticky(eventName)                  → void
 *
 * API (global fallback — for non-module scripts like 3pa loaded via usercentrics):
 *   window.__pubsub.subscribe(eventName, callback)  → unsubscribe function
 *   window.__pubsub.publish(eventName, data, opts)  → void
 *   window.__pubsub.clearSticky(eventName)          → void
 *
 * Sticky Events:
 *   When `publish` is called with `{ sticky: true }`, the data is stored.
 *   Any future `subscribe` call for that event will immediately invoke the
 *   callback with the stored data, solving late-initialization race conditions.
 *
 *   Current sticky events:
 *     product-overview:initialized, product-overview:variant-change,
 *     product-overview:no-variant, cart-state:loaded, cart-state:changed,
 *     cart-state:initial-load, cart-system:ready
 *
 * Event Catalog:
 *   product-overview:initialized                       (sticky)  → product data + variant
 *   product-overview:variant-change                    (sticky)  → variant data
 *   product-overview:no-variant                        (sticky)  → requested title
 *   product-overview:swatch-change                               → swatch data + variant
 *   product-option-selectors:option-change                       → selected option value IDs
 *   product-option-selectors:swatch-change                       → swatch + option value IDs
 *   product-option-selectors:invalid-option                      → reason
 *   product-option-selectors:option-availability-change          → productOverview ref
 *   product-promotion:change                                     → promotion data
 *   product-media:update-start                                   → productOverview ref
 *   product-media:update-success                                 → productOverview ref
 *   product-media:update-error                                   → productOverview ref + error
 *
 *   cart-state:loaded                              (sticky)  → { state }
 *   cart-state:changed                             (sticky)  → { state, previousState, changes, source, forced }
 *   cart-state:refreshed                                     → { state, reason, timestamp }
 *   cart-state:initial-load                        (sticky)  → { state, source }
 *   cart-state:error                                         → { error, operation, ...context }
 *   cart-state:item:adding                                   → { items, openDrawer }
 *   cart-state:item:added                                    → { items, openDrawer, state }
 *   cart-state:item:removing                                 → { keys, items }
 *   cart-state:item:removed                                  → { keys, items, state }
 *   cart-state:item:changing                                 → { key, changes }
 *   cart-state:item:changed                                  → { key, changes, state }
 *   cart-state:item:updated                                  → { updates, state }
 *   cart-state:cart:updating                                 → { updates, note, attributes, discount }
 *   cart-state:cart:updated                                  → { updates, note, attributes, discount, state }
 *   cart-state:note:updated                                  → { note, state }
 *   cart-state:attributes:updated                            → { attributes, state }
 *   cart-state:discount:updated                              → { discount, state }
 *   cart-system:ready                              (sticky)  → { cartStateManager, cartDrawer }
 *   cart-drawer:opened                                       → { drawer, state }
 *   cart-item:bundle-remove                                  → { bundleId, isLoyaltyBundle, membershipHandle }
 *   cart-item:bundle-update                                  → { bundleId, newQuantity }
 *   cart-item:bundle-operation-complete                      → { bundleId, operation, success, error? }
 *   cart-item:quantity-change-requested                      → { key, newQuantity, debounceMs, requestId }
 *   cart-item:quantity-change-confirmed                      → { key, newQuantity, requestId, success, result }
 *   cart-items:state-updated                                 → { state }
 *   cart:membership-bundle:added                             → { membership }
 *   cart:membership-bundle:removed                           → { membership }
 *
 * Scoping:
 *   The pub-sub is global — there is one shared channel per event name.
 *   For instance-scoped filtering (e.g., multiple product-overviews on a page),
 *   publishers include a reference (like the element) in the payload, and
 *   subscribers filter inside their callback:
 *
 *     subscribe('product-overview:variant-change', (data) => {
 *       if (data.productOverview !== this.productOverview) return;
 *       // handle event
 *     });
 *
 * Error Isolation:
 *   Each subscriber callback is wrapped in try/catch so one failing subscriber
 *   does not prevent others from receiving the event.
 *
 * @example
 *   // ES module usage (preferred)
 *   import { publish, subscribe, clearSticky } from '@utilities/events';
 *
 *   // Publisher
 *   publish('product-overview:variant-change', { productOverview: this, variant }, { sticky: true });
 *
 *   // Subscriber (returns unsubscribe function)
 *   const unsubscribe = subscribe('product-overview:variant-change', (data) => {
 *     console.log('Variant changed:', data.variant);
 *   });
 *
 *   // Cleanup
 *   unsubscribe();
 *   clearSticky('product-overview:variant-change');
 *
 * @example
 *   // Global fallback for non-module scripts (3pa via usercentrics)
 *   if (window.__pubsub?.subscribe) {
 *     window.__pubsub.subscribe('product-overview:initialized', (data) => {
 *       console.log('Product initialized:', data.product);
 *     });
 *   }
 */

const subscribers = {};
const stickyEvents = {};

export const EVENTS = Object.freeze({
  // Product Overview
  PRODUCT_OVERVIEW_INITIALIZED: 'product-overview:initialized',
  PRODUCT_OVERVIEW_VARIANT_CHANGE: 'product-overview:variant-change',
  PRODUCT_OVERVIEW_NO_VARIANT: 'product-overview:no-variant',
  PRODUCT_OVERVIEW_SWATCH_CHANGE: 'product-overview:swatch-change',
  // Product Option Selectors
  PRODUCT_OPTION_SELECTORS_OPTION_CHANGE: 'product-option-selectors:option-change',
  PRODUCT_OPTION_SELECTORS_SWATCH_CHANGE: 'product-option-selectors:swatch-change',
  PRODUCT_OPTION_SELECTORS_INVALID_OPTION: 'product-option-selectors:invalid-option',
  PRODUCT_OPTION_SELECTORS_OPTION_AVAILABILITY_CHANGE: 'product-option-selectors:option-availability-change',
  // Product Promotion
  PRODUCT_PROMOTION_CHANGE: 'product-promotion:change',
  // Product Media
  PRODUCT_MEDIA_UPDATE_START: 'product-media:update-start',
  PRODUCT_MEDIA_UPDATE_SUCCESS: 'product-media:update-success',
  PRODUCT_MEDIA_UPDATE_ERROR: 'product-media:update-error',
  // Cart State Manager
  CART_STATE_LOADED: 'cart-state:loaded',
  CART_STATE_CHANGED: 'cart-state:changed',
  CART_STATE_REFRESHED: 'cart-state:refreshed',
  CART_STATE_INITIAL_LOAD: 'cart-state:initial-load',
  CART_STATE_ERROR: 'cart-state:error',
  CART_STATE_ITEM_ADDING: 'cart-state:item:adding',
  CART_STATE_ITEM_ADDED: 'cart-state:item:added',
  CART_STATE_ITEM_REMOVING: 'cart-state:item:removing',
  CART_STATE_ITEM_REMOVED: 'cart-state:item:removed',
  CART_STATE_ITEM_CHANGING: 'cart-state:item:changing',
  CART_STATE_ITEM_CHANGED: 'cart-state:item:changed',
  CART_STATE_ITEM_UPDATED: 'cart-state:item:updated',
  CART_STATE_CART_UPDATING: 'cart-state:cart:updating',
  CART_STATE_CART_UPDATED: 'cart-state:cart:updated',
  CART_STATE_NOTE_UPDATED: 'cart-state:note:updated',
  CART_STATE_ATTRIBUTES_UPDATED: 'cart-state:attributes:updated',
  CART_STATE_DISCOUNT_UPDATED: 'cart-state:discount:updated',
  CART_SYSTEM_READY: 'cart-system:ready',
  // Cart Drawer
  CART_DRAWER_OPENED: 'cart-drawer:opened',
  // Cart Item
  CART_ITEM_BUNDLE_REMOVE: 'cart-item:bundle-remove',
  CART_ITEM_BUNDLE_UPDATE: 'cart-item:bundle-update',
  CART_ITEM_BUNDLE_OPERATION_COMPLETE: 'cart-item:bundle-operation-complete',
  CART_ITEM_QUANTITY_CHANGE_REQUESTED: 'cart-item:quantity-change-requested',
  CART_ITEM_QUANTITY_CHANGE_CONFIRMED: 'cart-item:quantity-change-confirmed',
  // Cart Items
  CART_ITEMS_STATE_UPDATED: 'cart-items:state-updated',
  // Membership
  CART_MEMBERSHIP_BUNDLE_ADDED: 'cart:membership-bundle:added',
  CART_MEMBERSHIP_BUNDLE_REMOVED: 'cart:membership-bundle:removed',
});

/**
 * Subscribe to a named event.
 *
 * If a sticky value already exists for the event, the callback is invoked
 * immediately (synchronously) with that value.
 *
 * @param {string} eventName - The event topic to subscribe to.
 * @param {function} callback - Invoked with the published data object.
 * @returns {function} An unsubscribe function. Call it to remove this subscription.
 */
export function subscribe(eventName, callback) {
  if (!subscribers[eventName]) {
    subscribers[eventName] = [];
  }

  subscribers[eventName].push(callback);

  if (eventName in stickyEvents) {
    try {
      callback(stickyEvents[eventName]);
    } catch (error) {
      console.error(`[pub-sub] Error in sticky replay for "${eventName}":`, error);
    }
  }

  return () => {
    const list = subscribers[eventName];
    if (!list) return;
    const index = list.indexOf(callback);
    if (index !== -1) {
      list.splice(index, 1);
    }
  };
}

/**
 * Publish data to all current subscribers of a named event.
 *
 * @param {string} eventName - The event topic to publish to.
 * @param {*} data - The data payload delivered to each subscriber callback.
 * @param {Object} [options] - Optional configuration.
 * @param {boolean} [options.sticky=false] - When true, stores the data so future
 *   subscribers receive it immediately upon registration.
 */
export function publish(eventName, data, options) {
  if (options?.sticky) {
    stickyEvents[eventName] = data;
  }

  const list = subscribers[eventName];
  if (!list || list.length === 0) return;

  for (const callback of [...list]) {
    try {
      callback(data);
    } catch (error) {
      console.error(`[pub-sub] Error in subscriber for "${eventName}":`, error);
    }
  }
}

/**
 * Remove the stored sticky value for a named event.
 *
 * Use this to prevent stale replays — for example, when `variant-change`
 * and `no-variant` are mutually exclusive states, publishing one should
 * clear the other's sticky value.
 *
 * @param {string} eventName - The event topic whose sticky value to clear.
 */
export function clearSticky(eventName) {
  delete stickyEvents[eventName];
}

/**
 * Global fallback for non-module scripts (e.g. 3pa scripts loaded via usercentrics).
 * Module-based consumers should use `import { subscribe } from '@utilities/events'` instead.
 */
if (typeof window !== 'undefined') {
  window.__pubsub = { subscribe, publish, clearSticky, EVENTS };
}
